import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, MessageCircle, CheckCircle, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddQuestionDialog from './AddQuestionDialog';

const QuestionsTab = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await API.get('/medical/questions');
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (questionData) => {
    try {
      await API.post('/medical/questions', questionData);
      toast.success('Question added');
      fetchQuestions();
      setShowAdd(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add question');
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/medical/questions/${id}`);
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  if (loading) {
    return <div className="space-y-4">{[1, 2].map((i) => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  }

  if (questions.length === 0) {
    return (
      <>
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-[#B8E6D5] rounded-full flex items-center justify-center mb-4">
            <MessageCircle className="h-8 w-8 text-gray-700" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No questions recorded</h3>
          <p className="text-gray-600 mb-4">Prepare questions for doctor visits</p>
          <Button onClick={() => setShowAdd(true)} className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
            <Plus className="h-4 w-4 mr-2" />
            Add Question
          </Button>
        </div>
        <AddQuestionDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
      </>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Questions for Doctor</h3>
        <Button onClick={() => setShowAdd(true)} size="sm" className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
          <Plus className="h-4 w-4 mr-2" />
          Add Question
        </Button>
      </div>

      <div className="space-y-4">
        {questions.map((question) => (
          <div key={question.id} className="border border-gray-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-lg mb-2">{question.question_text}</h4>
                <div className="flex items-center space-x-3 text-sm text-gray-600">
                  {question.doctor_name && (
                    <span>Dr. {question.doctor_name}</span>
                  )}
                  {question.visit_date && (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {question.visit_date}
                    </div>
                  )}
                </div>
              </div>
              {question.answer_text ? (
                <Badge className="bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Answered
                </Badge>
              ) : (
                <Badge variant="outline">Pending</Badge>
              )}
            </div>

            {question.answer_text && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg mt-3">
                <p className="text-sm font-medium text-green-900 mb-1">Answer:</p>
                <p className="text-sm text-green-800">{question.answer_text}</p>
              </div>
            )}

            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" onClick={() => handleDelete(question.id)} className="text-red-600 hover:text-red-700">
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AddQuestionDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
    </>
  );
};

export default QuestionsTab;